import React, { useCallback, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { addToFavorites } from 'api'
import classNames from 'classnames'
import { AnimatePresence } from 'framer-motion'

import LightBox from 'components/LightBox'
import { changeStage } from 'store/reducers/stageReducer'
import { useAppDispatch } from 'hooks/store'
import { BaseAsset, Status } from 'types/Response'
import Loader from '../common/Loader'
import FinishResultCard from '../FinishResultCard'

import c from './GenerationFinish.module.scss'

interface GenerationFinishProps {
  currentAssetIndex: number
  text?: string
  assets?: BaseAsset[]
  onGoBack: () => void
  sharedSlides: number[]
  onGenerateMore: () => void
  isLoading: boolean
  setSharedSlides: React.Dispatch<React.SetStateAction<number[]>>
  onTextChange: (newText: string) => void
  generationStatus: Status
}

function GenerationFinish({
  currentAssetIndex,
  onGoBack,
  isLoading,
  assets = [],
  text = '',
  onGenerateMore,
  onTextChange,
  sharedSlides,
  setSharedSlides,
  generationStatus,
}: GenerationFinishProps) {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const [currentSlide, setCurrentSlide] = useState<number>(currentAssetIndex)
  const [isFavorited, setIsFavorited] = useState<number[]>([])
  const [isLightboxOpen, setIsLightboxOpen] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState<{
    [key: number]: boolean
  }>(assets.reduce((acc, asset) => ({ ...acc, [asset.id]: false }), {}))
  const [lightboxIndex, setLightboxIndex] = useState(0)

  const { pathname } = useLocation()

  const mutationAddToFavorites = useMutation<void, Error>(() => {
    return addToFavorites(assets?.[currentSlide]?.id)
  })

  const handleAddToFavorites = () => {
    if (isFavorited.includes(currentSlide)) return

    mutationAddToFavorites
      .mutateAsync()
      .then(() => setIsFavorited((prev) => [...prev, currentSlide]))
  }

  const variationsHandler = (assetId: number) => {
    dispatch(changeStage('idle'))
    navigate(`/magic-variations/${assetId}`)
  }

  const handleImageLoaded = useCallback((assetId: number) => {
    setIsImageLoaded((prevState) => ({ ...prevState, [assetId]: true }))
  }, [])

  const handleFullScreenOpen = (index: number) => {
    setLightboxIndex(index)
    setIsLightboxOpen(true)
  }

  const handleFullScreenClose = () => {
    setIsLightboxOpen(false)
  }

  const moveToNext = () => {
    setLightboxIndex((prevIndex) =>
      prevIndex === assets.length - 1 ? 0 : prevIndex + 1,
    )
  }

  const moveToPrev = () => {
    setLightboxIndex((prevIndex) =>
      prevIndex === 0 ? assets.length - 1 : prevIndex - 1,
    )
  }

  const renderAssets = useMemo(() => {
    return assets.map((asset, index) => (
      <FinishResultCard
        key={asset.id}
        asset={asset}
        index={index}
        isImageLoaded={isImageLoaded}
        handleImageLoaded={handleImageLoaded}
        handleFullScreenOpen={handleFullScreenOpen}
        variationsHandler={variationsHandler}
      />
    ))
  }, [assets, isImageLoaded, handleFullScreenOpen, variationsHandler])

  return (
    <div
      className={classNames({
        [c.finishingStage]: !isLoading,
        [c.loadingStage]: isLoading,
      })}
    >
      {isLoading && assets ? (
        <div className={c.loader}>
          <Loader />
        </div>
      ) : (
        <div className={c.content}>
          {generationStatus !== 'pending' && renderAssets}
          <AnimatePresence>
            {isLightboxOpen && assets?.[lightboxIndex]?.url && (
              <LightBox
                imageUrl={assets[lightboxIndex].url || 'defaultImg'}
                onCloseRequest={handleFullScreenClose}
                nextUrl={assets[(lightboxIndex + 1) % assets.length].url}
                prevUrl={
                  assets[(lightboxIndex + assets.length - 1) % assets.length]
                    .url
                }
                onMovePrevRequest={moveToPrev}
                onMoveNextRequest={moveToNext}
                sourceUrl={
                  pathname.includes('magic-variations')
                    ? assets[lightboxIndex].source_url
                    : ''
                }
              />
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  )
}

export default GenerationFinish
